import { useEffect, useState } from 'preact/hooks';
import {
  listAsks,
  listAttentionItems,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  type AttentionItem,
  type LinkRow,
  type RecordRow,
} from '../lib/api';
import { routePath } from '../routes/route';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { resolveAttentionDestination } from '../lib/attentionRoute';
import { askAsAttentionItem } from '../lib/asks';
import { activeOnly } from '../lib/retired';
import { DEPOSIT_BANDS, RECEIVABLE_BANDS, tallyByAge } from '../lib/aging';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './RexxHome.css';

// S1 — THE ADMIN HOME (wf02 §02, blessed 2026-08-06): "a work surface, not
// a dashboard of tiles". The generic answer is six KPI cards — a screen
// Wendy looks at and then leaves, because none of it is a thing she can
// do. So this is built from the three questions she actually opens the
// app with — what needs me, where is the money, and what came in from the
// field — and every line on it is a doorway rather than a readout.
//
//   1. NEEDS YOU — attention items in severity order, five at a time with
//      a real expander (the cap is the design: "a count you cannot act on
//      is noise, so there isn't one").
//   2. The money bands — awaiting payment / awaiting deposit / deposited,
//      counts + totals from the real work-order records; each band is a
//      doorway to that filtered list. S14 hung the AGED block underneath
//      them (see the note above the money column below).
//   3. LANDING TODAY — today's visits, newest first, gold-dotted when
//      their capture landed and dash-dotted while still out ("pending
//      items show as pending and stay visible until they land — the
//      offline rule, made literal").
//
// Data: existing production reads only (/api/attention, /records?type_key=
// — paged to the full truth, see listAllRecordsByType). No bespoke
// aggregate route.

export interface RexxHomeProps {
  tenantId: string;
  userName: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const NEEDS_YOU_CAP = 5;

// Severity rank for the needs-you ordering (the server's three values).
const SEVERITY_RANK: Record<AttentionItem['severity'], number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

const SEVERITY_VAR: Record<AttentionItem['severity'], string> = {
  critical: 'var(--status-critical)',
  warning: 'var(--status-warning)',
  info: 'var(--status-neutral)',
};

// S14 — WHERE THE MONEY IS, AND HOW OLD IT IS.
//
// The three STATE bands below are unchanged: they answer "which stage is
// this money at", which is the question the office's own vocabulary is
// built from, and each stays a doorway to that filtered work-order list.
//
// What S1 could not do was age them — hvac_invoice carried no date of any
// kind, so "counts by lifecycle state" was not a design choice, it was the
// only thing the data supported. wf02 §02 is explicit that age is the real
// instrument ("what you do about 30 days is different from 90"), and S14
// added hvac_invoice.issued_date so it finally exists. The AGED block
// underneath the state bands is that instrument.
//
// WHY THE AGED BLOCK COUNTS INVOICES AND THE BANDS COUNT WORK ORDERS —
// and why the two are not merged. The date lives on the invoice; the
// lifecycle state lives on the work order; and the only thing joining them
// is a `invoice_work_order` LINK, which this API exposed one record at a
// time. So the aged block is computed from the invoices alone, on the
// invoice's OWN arithmetic — `total - amount_paid > 0` is money still
// outstanding, no join required and no state guessed. On a tenant where
// every invoiced job carries one invoice the two counts agree exactly;
// where they don't, each half still states its own unit rather than
// blending them into a number that means neither.
//
// An invoice with NO issued date lands in "Age unknown" — never in the
// 0–30 band. Every imported V6 row is exactly that (the workbook carries
// no invoice date; see packages/server/src/dev/v6/manifest.ts), so this
// bucket is the shape of Alan's real history, not an edge case.
//
// ---------------------------------------------------------------------
// S15 — THE DEPOSIT CLOCK: the half of wf02's money column S14 left owed.
//
// "742 awaiting deposit" is one of the two biggest facts in Alan's
// business, and until now it could only be COUNTED, never aged. The age of
// undeposited money is the day it arrived — `hvac_payment.received_date`,
// which S14 added — and knowing WHICH payments belong to the
// awaiting-deposit pile is exactly the payment -> work-order join that had
// no bulk read, so S14 wrote the gap down and left the pile unaged.
//
// It has one now: `GET /records?...&include=links&link_type=payment_work_order`
// returns the pile's work orders and their payment links in the SAME
// request the pile was already being read with, so the whole clock costs
// ONE extra read (the payments themselves, for amount and date). The bands
// are wf02's own — This week / 1–2 weeks / Over 2 weeks — and a payment
// whose received date nobody recorded lands in its own bucket, never in
// "This week", by the same rule and for the same reason as the invoices.
//
// Its unit is PAYMENTS, and the block says so, exactly as the aged invoice
// block says "invoices": the state band above counts work orders, and
// blending the two would produce a number that means neither.
/** hvac_payment's direct lookup at the job it settles (the pack keys it
 *  `payment_work_order` — see recordTypes.ts's RELATIONSHIP-KEY UNIQUENESS
 *  note for why none of these keys is the bare word). */
const PAYMENT_WO_LINK = 'payment_work_order';

const MONEY_BANDS = [
  { state: 'awaiting_payment', label: 'Awaiting payment', color: 'var(--status-warning)' },
  { state: 'awaiting_deposit', label: 'Awaiting deposit', color: 'var(--status-neutral)' },
  { state: 'deposited', label: 'Deposited', color: 'var(--status-good)' },
] as const;

function money(n: number): string {
  return n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function relativeWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function humanize(key: string): string {
  return key.replace(/_/g, ' ');
}

/** A visit's one meaningful moment for "today" purposes — when it actually
 * ended, else started, else was scheduled to start. */
function visitWhen(v: RecordRow): string | null {
  const f = v.fields;
  const raw = f['actual_end'] ?? f['actual_start'] ?? f['scheduled_start'];
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  );
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function bandTotal(rows: RecordRow[]): number {
  return rows.reduce((sum, r) => {
    const v = Number(r.fields['invoice_total_sum'] ?? 0);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);
}

function num(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** What is still owed on an invoice: its total less the payments its own
 * `amount_paid` rollup has already counted. Sub-cent remainders are not a
 * debt — floating-point currency arithmetic leaves them behind and a
 * chase list full of $0.00 rows is noise. */
function outstandingOf(inv: RecordRow): number {
  return num(inv.fields['total']) - num(inv.fields['amount_paid']);
}

/** S15 — the deposit clock's own join, as a pure function: the payments
 * that belong to the awaiting-deposit pile. A payment reaches its work order
 * by the `payment_work_order` lookup, so the pile's own link expansion names
 * them; anything else in the payments list (a part payment against an
 * unpaid invoice, a payment on a closed job) is not money waiting in the
 * drawer and is not counted here. */
function depositPayments(
  awaitingDepositIds: readonly string[],
  linksByRecordId: Record<string, LinkRow[]>,
  payments: readonly RecordRow[]
): RecordRow[] {
  const wanted = new Set<string>();
  for (const woId of awaitingDepositIds) {
    for (const l of linksByRecordId[woId] ?? []) {
      if (l.link_type !== PAYMENT_WO_LINK) continue;
      wanted.add(l.src_id === woId ? l.dst_id : l.src_id);
    }
  }
  return payments.filter((p) => wanted.has(p.id));
}

export function RexxHome({ tenantId, userName, onNavigate }: RexxHomeProps) {
  const [state, setState] = useState<LoadState>('loading');
  // S15: the FAILURE itself, not a bare message — a refused read says which
  // refusal it was, and a 429 says it is temporary. This screen never
  // renders a failed read as an empty day.
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [attention, setAttention] = useState<AttentionItem[]>([]);
  const [bands, setBands] = useState<Record<string, RecordRow[]>>({});
  const [openInvoices, setOpenInvoices] = useState<RecordRow[]>([]);
  const [drawerPayments, setDrawerPayments] = useState<RecordRow[]>([]);
  const [todaysVisits, setTodaysVisits] = useState<RecordRow[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState('loading');
      setFailure(null);
      try {
        const [items, asks, awaitingPayment, awaitingDeposit, deposited, visits, invoices, payments] =
          await Promise.all([
            listAttentionItems(tenantId),
            // S11: an ask routed to me IS "a receipt waiting on
            // authorization" — wf02 §02's own first example of a needs-you
            // item. The server's attention producer knows nothing about asks
            // (plugins/attention.ts contains no mention of them) and this
            // chunk does not teach it: the fold is CLIENT-SIDE, one extra
            // read, into the ONE list. Open only — an answered ask is not
            // work.
            listAsks(tenantId, 'routed_to_me', 'open'),
            listAllRecordsByType(tenantId, 'hvac_work_order', 'awaiting_payment'),
            // S15: the awaiting-deposit pile now arrives WITH the payment
            // links that age it — same request, one `include=links`, and the
            // deposit clock exists. Nothing else about this read changed.
            listAllRecordsWithLinks(tenantId, 'hvac_work_order', {
              lifecycleState: 'awaiting_deposit',
              linkTypes: [PAYMENT_WO_LINK],
            }),
            listAllRecordsByType(tenantId, 'hvac_work_order', 'deposited'),
            listAllRecordsByType(tenantId, 'hvac_visit'),
            // S14: the aged block's source. Every invoice, not a state
            // filter — a seeded/imported invoice sits in 'draft' whatever
            // its work order's state is (rexx.invoice.send is EXTERNAL, so
            // nothing walks it to 'sent'), which makes the invoice's own
            // lifecycle useless for deciding what is outstanding. Its
            // arithmetic is not: total less amount_paid.
            listAllRecordsByType(tenantId, 'hvac_invoice'),
            // S15: the deposit clock's amounts and dates. Every payment,
            // then narrowed to the pile by the links above — a payment
            // carries no state of its own to filter on.
            listAllRecordsByType(tenantId, 'hvac_payment'),
          ]);
        if (cancelled) return;
        setAttention(
          [...items, ...asks.map(askAsAttentionItem)].sort(
            (a, b) =>
              SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
              // Newest first within a severity; ISO strings compare
              // lexicographically. localeCompare-style three-way so ties
              // return 0 (a comparator that never says "equal" is
              // implementation-defined chaos on equal keys).
              (a.lastRaisedAt < b.lastRaisedAt ? 1 : a.lastRaisedAt > b.lastRaisedAt ? -1 : 0)
          )
        );
        // S11 Part B: a RETIRED work order is not money anyone is chasing.
        // The same leak the lists had, in arithmetic form — core's
        // listRecords excludes only status 'deleted', so an archived order
        // was still being counted and summed here.
        const depositPile = activeOnly(awaitingDeposit.records);
        setBands({
          awaiting_payment: activeOnly(awaitingPayment),
          awaiting_deposit: depositPile,
          deposited: activeOnly(deposited),
        });
        // Same retirement rule as the bands: a retired invoice is not
        // money anyone is chasing.
        setOpenInvoices(activeOnly(invoices).filter((inv) => outstandingOf(inv) > 0.005));
        // S15 — the deposit clock. Retired payments leave it for the same
        // reason retired invoices leave the aged block.
        setDrawerPayments(
          depositPayments(
            depositPile.map((wo) => wo.id),
            awaitingDeposit.linksByRecordId,
            activeOnly(payments)
          )
        );
        const today = activeOnly(visits).filter((v) => {
          const when = visitWhen(v);
          return when !== null && isToday(when);
        });
        today.sort((a, b) => ((visitWhen(a) ?? '') < (visitWhen(b) ?? '') ? 1 : -1));
        setTodaysVisits(today);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setState('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, retryNonce]);

  // Every needs-you line is a doorway. S7: the resolution table is the
  // SHARED resolver (lib/attentionRoute.ts, told this tenant wears the Rexx
  // pack) — detail screens for the four records that have one, the schema-
  // driven editor for the rest, item-type fallbacks (an expiring ticket
  // lands on its tech's screen) so no line is inert. A subject even the
  // resolver can't place renders as a plain line — graceful, never a dead
  // handle.
  function attentionAction(item: AttentionItem): (() => void) | undefined {
    const dest = resolveAttentionDestination(item, 'rexx');
    if (!dest) return undefined;
    if (dest.kind === 'editor') {
      return () => openRecordEditor({ mode: 'edit', typeKey: dest.typeKey, id: dest.id });
    }
    return () => onNavigate(dest.path);
  }

  if (state === 'loading') {
    return (
      <div class="rexx-home">
        <div class="rexx-home__eyebrow">Today</div>
        <div class="rexx-home__say">One moment…</div>
      </div>
    );
  }

  if (state === 'error' && failure !== null) {
    // NOT "nothing needs you", NOT an empty money column. On 2026-08-13 a
    // rate-limited read rendered as absence and told Huan his shop was
    // empty; this says which refusal it was, that a 429 passes, and offers
    // the retry that actually works.
    return (
      <div class="rexx-home">
        <div class="rexx-home__eyebrow">Today</div>
        <div class="rexx-home__say">
          {failure.headline}
          <br />
          <span class="rexx-home__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="rexx-home__more" onClick={() => setRetryNonce((n) => n + 1)}>
            Read today again
          </button>
        ) : null}
      </div>
    );
  }

  const shown = expanded ? attention : attention.slice(0, NEEDS_YOU_CAP);
  const hidden = attention.length - Math.min(NEEDS_YOU_CAP, attention.length);
  const pendingToday = todaysVisits.filter((v) => v.lifecycle_state !== 'completed');
  const bandRows = MONEY_BANDS.map((band) => {
    const rows = bands[band.state] ?? [];
    return { ...band, count: rows.length, total: bandTotal(rows) };
  });
  const maxTotal = Math.max(...bandRows.map((b) => b.total), 1);

  // The aged block. `tallyByAge` owns the rule that a null date is its own
  // bucket (lib/aging.ts) — this screen only decides how to say it.
  const aged = tallyByAge(
    openInvoices,
    (inv) => inv.fields['issued_date'],
    outstandingOf,
    new Date(),
    RECEIVABLE_BANDS
  );
  const agedRows = [
    ...aged.bands,
    // Rendered only when it has rows — an always-on empty "unknown" line
    // is clutter — but NEVER folded into a band when it does.
    ...(aged.unknown.count > 0
      ? [{ key: 'unknown', label: 'Age unknown', color: 'var(--mute)', ...aged.unknown }]
      : []),
  ];
  const maxAged = Math.max(...agedRows.map((b) => b.total), 1);

  // S15 — the deposit clock, on wf02's own three bands and the same
  // null-date rule: money whose arrival date nobody wrote down is its own
  // line, never the newest one.
  const drawer = tallyByAge(
    drawerPayments,
    (p) => p.fields['received_date'],
    (p) => num(p.fields['amount']),
    new Date(),
    DEPOSIT_BANDS
  );
  const drawerRows = [
    ...drawer.bands,
    ...(drawer.unknown.count > 0
      ? [{ key: 'unknown', label: 'Received date unknown', color: 'var(--mute)', ...drawer.unknown }]
      : []),
  ];
  const maxDrawer = Math.max(...drawerRows.map((b) => b.total), 1);

  return (
    <div class="rexx-home">
      <div class="rexx-home__eyebrow">Today</div>
      <div class="rexx-home__say">{userName} · Ether Inc</div>

      <div class="rexx-home__grid">
        <div class="rexx-home__col">
          <div class="rexx-home__sec">
            <span>Needs you</span>
            <span class="rexx-home__mini">
              {attention.length > NEEDS_YOU_CAP && !expanded
                ? `${NEEDS_YOU_CAP} of ${attention.length}`
                : `${attention.length}`}
            </span>
          </div>
          {shown.length === 0 ? (
            <div class="rexx-home__empty">Nothing needs you right now.</div>
          ) : (
            shown.map((item) => {
              const act = attentionAction(item);
              const body = (
                <>
                  <span class="rexx-home__stripe" style={{ background: SEVERITY_VAR[item.severity] }} />
                  <span
                    class="rexx-home__tag"
                    style={{
                      color: item.severity === 'info' ? 'var(--mute)' : SEVERITY_VAR[item.severity],
                    }}
                  >
                    {humanize(item.itemType)}
                  </span>
                  <span class="rexx-home__attn-tx">
                    <b>{item.reason ?? humanize(item.itemType)}</b>
                    <small>{relativeWhen(item.lastRaisedAt)}</small>
                  </span>
                </>
              );
              return act ? (
                <button type="button" class="rexx-home__attn" key={item.id} onClick={act}>
                  {body}
                </button>
              ) : (
                <div class="rexx-home__attn rexx-home__attn--inert" key={item.id}>
                  {body}
                </div>
              );
            })
          )}
          {hidden > 0 ? (
            <button
              type="button"
              class="rexx-home__more"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? '– show fewer ▴' : `+ ${hidden} more ▾`}
            </button>
          ) : null}

          <div class="rexx-home__sec rexx-home__sec--later">
            <span>Landing today</span>
            <span class="rexx-home__mini">from the field</span>
          </div>
          {todaysVisits.length === 0 ? (
            <div class="rexx-home__empty">Nothing has landed from the field yet today.</div>
          ) : (
            todaysVisits.map((v) => {
              const landed = v.lifecycle_state === 'completed';
              const when = visitWhen(v);
              const line =
                typeof v.fields['work_done'] === 'string' && (v.fields['work_done'] as string).length > 0
                  ? (v.fields['work_done'] as string)
                  : v.display_name;
              return (
                <div class="rexx-home__land" key={v.id}>
                  <span class={landed ? 'rexx-home__dot' : 'rexx-home__dot rexx-home__dot--pend'} />
                  <span class="rexx-home__land-tx">{line}</span>
                  <span class="rexx-home__land-t">
                    {landed ? (when ? timeOf(when) : '') : humanize(v.lifecycle_state ?? 'pending')}
                  </span>
                </div>
              );
            })
          )}
          {pendingToday.length > 0 ? (
            <div class="rexx-home__pending-note">
              <b>
                {pendingToday.length === 1
                  ? 'One capture is still in the field.'
                  : `${pendingToday.length} captures are still in the field.`}
              </b>{' '}
              They stay visible here until they land. Nothing is lost.
            </div>
          ) : null}

          {/* S5b — THE DOOR TO THE CONVERSATIONS. The office surface has no
              rail slot: the blessed rails (wf02 §01b admin · dispatch ·
              owner) carry no chat tab, and adding one is a manifest change
              that chunk deliberately is not. So it is reached from Today,
              exactly as the asks inbox is — and it has to be reached from
              somewhere, because a surface nothing links to is the same
              "built and never wired" S5b exists to end. Costs no request:
              it is a link, not a read. */}
          <div class="rexx-home__sec rexx-home__sec--later">
            <span>Conversations</span>
            <span class="rexx-home__mini">job threads · direct lanes</span>
          </div>
          <button
            type="button"
            class="rexx-home__door"
            data-testid="rexx-home-conversations"
            onClick={() => onNavigate(routePath({ screen: 'chat' }))}
          >
            <span class="rexx-home__attn-tx">
              <b>Open conversations</b>
              <small>What the field is saying, and what the office said back.</small>
            </span>
            <span class="rexx-home__door-go" aria-hidden="true">
              ›
            </span>
          </button>
        </div>

        <div class="rexx-home__col">
          <div class="rexx-home__sec">
            <span>The money</span>
            <span class="rexx-home__mini">work orders</span>
          </div>
          {bandRows.map((band) => (
            <button
              type="button"
              class="rexx-home__band"
              key={band.state}
              onClick={() => onNavigate(routePath({ screen: 'workOrders', state: band.state }))}
            >
              <span class="rexx-home__band-k">{band.label}</span>
              <span class="rexx-home__band-bar">
                <i
                  style={{
                    width: `${Math.max(band.total > 0 ? 4 : 0, Math.round((band.total / maxTotal) * 100))}%`,
                    background: band.color,
                  }}
                />
              </span>
              <span class="rexx-home__band-v">
                {band.count} · ${money(band.total)}
              </span>
            </button>
          ))}

          <div class="rexx-home__sec rexx-home__sec--later">
            <span>Outstanding</span>
            <span class="rexx-home__mini">invoices, by age</span>
          </div>
          {aged.count === 0 ? (
            <div class="rexx-home__empty">Nothing is outstanding.</div>
          ) : (
            agedRows.map((row) => (
              <div class="rexx-home__band rexx-home__band--aged" key={row.key}>
                <span class="rexx-home__band-k">{row.label}</span>
                <span class="rexx-home__band-bar">
                  <i
                    style={{
                      width: `${Math.max(row.total > 0 ? 4 : 0, Math.round((row.total / maxAged) * 100))}%`,
                      background: row.color,
                    }}
                  />
                </span>
                <span class="rexx-home__band-v">
                  {row.count} · ${money(row.total)}
                </span>
              </div>
            ))
          )}

          {/* S15 — the deposit clock. Its unit is PAYMENTS and it says so:
              the state band above counts work orders, this counts the money
              actually sitting in the drawer. */}
          <div class="rexx-home__sec rexx-home__sec--later">
            <span>In the drawer</span>
            <span class="rexx-home__mini">payments, by age</span>
          </div>
          {drawer.count === 0 ? (
            <div class="rexx-home__empty">Nothing is waiting to be deposited.</div>
          ) : (
            drawerRows.map((row) => (
              <div class="rexx-home__band rexx-home__band--aged" key={row.key}>
                <span class="rexx-home__band-k">{row.label}</span>
                <span class="rexx-home__band-bar">
                  <i
                    style={{
                      width: `${Math.max(row.total > 0 ? 4 : 0, Math.round((row.total / maxDrawer) * 100))}%`,
                      background: row.color,
                    }}
                  />
                </span>
                <span class="rexx-home__band-v">
                  {row.count} · ${money(row.total)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
